/**
 * Auto-mark approved card POs purchased when a card charge is linked.
 */

import { isCardPurchasePayment, isCompanyCardPayment, isProjectCardPayment } from './purchasingPolicy';
import type { PurchaseRequisition } from '../types/purchasing';

export const CARD_PO_AUTO_MATCH_AMOUNT_TOLERANCE = 5;

export function roundMoney(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export type LinkedCardChargeLite = {
  id: string;
  totalAmount?: number | null;
  txnDate?: string | null;
  cardLastFour?: string | null;
  bankAccountName?: string | null;
};

function parseCardLastFour(charge: LinkedCardChargeLite): string | null {
  const direct = (charge.cardLastFour || '').trim();
  if (/^\d{4}$/.test(direct)) return direct;
  const bank = charge.bankAccountName || '';
  const m = /\b(\d{4})\b/.exec(bank);
  return m ? m[1] : null;
}

function parsePurchasedDay(value: string | null | undefined): Date | null {
  const raw = (value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function isAutoPurchasableCardPo(
  po: {
    status?: string | null;
    paymentMethodId?: string | null;
    paymentMethod?: string | null;
  },
): boolean {
  if (po.status !== 'approved') return false;
  const method = {
    id: po.paymentMethodId || undefined,
    label: po.paymentMethod || undefined,
  };
  return (
    isProjectCardPayment(method)
    || isCompanyCardPayment(method)
    || (isCardPurchasePayment(method) && !isProjectCardPayment(method) && !isCompanyCardPayment(method))
  );
}

export function cardChargesMatchPoAmount(
  poAmount: number,
  chargeTotal: number,
  tolerance = CARD_PO_AUTO_MATCH_AMOUNT_TOLERANCE,
): boolean {
  return Math.abs(roundMoney(poAmount) - roundMoney(chargeTotal)) <= tolerance;
}

export type CardPoAutoPurchasePatch = {
  status: 'purchased';
  purchasedAmount: number;
  purchasedAt: Date;
  purchasedById: string;
  purchasedByName: string;
  purchaseVariance?: {
    poAmount: number;
    purchasedAmount: number;
    note: string;
  };
  cardLastFour?: string;
};

/** Sum linked charges; mark purchased at charge total; flag variance when PO total differs. */
export function buildCardPoAutoPurchasePatch(input: {
  po: Pick<PurchaseRequisition, 'amount' | 'paymentMethodId' | 'paymentMethod' | 'status'>;
  charges: LinkedCardChargeLite[];
  actorId: string;
  actorName: string;
  tolerance?: number;
}): CardPoAutoPurchasePatch | null {
  if (!isAutoPurchasableCardPo(input.po)) return null;
  if (!input.charges.length) return null;

  const chargeTotal = roundMoney(
    input.charges.reduce((sum, row) => sum + (Number(row.totalAmount) || 0), 0),
  );
  if (chargeTotal <= 0) return null;

  const poAmount = roundMoney(Number(input.po.amount) || 0);
  const dates = input.charges
    .map(row => parsePurchasedDay(row.txnDate))
    .filter((d): d is Date => !!d);
  const purchasedAt = dates.sort((a, b) => b.getTime() - a.getTime())[0] || new Date();

  const lastFour =
    input.charges.map(parseCardLastFour).find(v => !!v) || undefined;

  const patch: CardPoAutoPurchasePatch = {
    status: 'purchased',
    purchasedAmount: chargeTotal,
    purchasedAt,
    purchasedById: input.actorId,
    purchasedByName: input.actorName,
  };
  if (lastFour) patch.cardLastFour = lastFour;

  if (!cardChargesMatchPoAmount(poAmount, chargeTotal, input.tolerance)) {
    patch.purchaseVariance = {
      poAmount,
      purchasedAmount: chargeTotal,
      note: 'Card charge total differs from the PO amount; PO total unchanged.',
    };
  }
  return patch;
}
